import { InfoPage } from '@/components/info-page';
import { LoadingPage } from '@/components/loading-page';
import { EntityTable } from '@/components/table/entity-table';
import { TableErrorState } from '@/components/table/table-error-state';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { EmptyArray, getLearningPaths } from '@/lib/api-calls';
import { useErrorToast } from '@/lib/error-handling';
import { CreateOrEditLearningPath } from '@/routes/_layout/lessons/learning-paths/-components/create-or-edit-learning-path';
import { LearningPathWithLessons } from '@airreading/common/data';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { ArrowRight, Ellipsis } from 'lucide-react';
import { Suspense, useMemo } from 'react';
import { isAirAdmin } from '@airreading/common';
import { validateRouteRole } from '@/lib/routing';
import { lessonsSectionTabs } from '../-components/tabs';

export const Route = createFileRoute('/_layout/lessons/learning-paths/')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: LearningPathsIndex,
});

function LearningPathsIndex() {
  return (
    <Suspense fallback={<LoadingPage />}>
      <LearningPaths />
    </Suspense>
  );
}

const columnsHelper = createColumnHelper<LearningPathWithLessons>();
const columns = [
  columnsHelper.accessor('name', {
    id: 'name',
    header: 'Name',
    cell: (row) => row.getValue(),
  }),
  columnsHelper.accessor('learningPathLessons', {
    id: 'lessons',
    header: 'Lessons',
    cell: (row) => {
      const lessons = row.getValue();

      return (
        <div className="flex flex-wrap gap-x-2 gap-y-2">
          {lessons
            .toSorted((l1, l2) => l1.order - l2.order)
            .map(({ lesson }, index) => (
              <div key={lesson.id} className="flex items-center gap-x-2">
                <Badge variant={'outline'}>{lesson.name}</Badge>
                {index !== lessons.length - 1 && <ArrowRight className="w-2 h-2" />}
              </div>
            ))}
        </div>
      );
    },
  }),
  columnsHelper.accessor((row) => row, {
    id: 'actions',
    header: '',
    cell: ({ getValue }) => {
      return (
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger>
            <Button variant="ghost" className="flex h-8 w-8 p-0 data-[state=open]:bg-muted">
              <Ellipsis className="h-4 w-4" />
              <span className="sr-only">Open book action menu</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <CreateOrEditLearningPath
              from={{
                fromLearningPath: getValue(),
                mode: 'edit',
              }}
              trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Edit</DropdownMenuItem>}
            />
            <CreateOrEditLearningPath
              from={{
                fromLearningPath: getValue(),
                mode: 'copy',
              }}
              trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Clone</DropdownMenuItem>}
            />
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  }),
];

const LearningPaths = () => {
  const { data: response, error, refetch, isPending } = useQuery(getLearningPaths());
  const learningPaths = useMemo(() => response || EmptyArray, [response]);
  useErrorToast(error, 'Error loading learning paths. Please try again.');

  const table = useReactTable({
    data: learningPaths,
    columns,
    manualPagination: true,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      enableSorting: false,
    },
  });

  return (
    <InfoPage
      headerText="Learning Paths"
      headerActions={
        <>
          <CreateOrEditLearningPath />
        </>
      }
      tabs={lessonsSectionTabs()}
    >
      {
        <EntityTable
          table={table}
          hasError={!!error}
          isLoading={isPending}
          errorState={<TableErrorState refetchAction={refetch} />}
        />
      }
    </InfoPage>
  );
};
